import { trackProductEvent } from "@/lib/analytics/product-analytics";
import { buildClientDebugContext, normalizeAppError, toPlainText } from "@/lib/errors/normalize-app-error";
import { openErrorDetails } from "@/lib/errors/error-details-store";
import React, { ReactNode } from "react";
import { Toaster as SonnerToaster, toast as sonnerToast } from "sonner";

type SonnerError = typeof sonnerToast.error;
type SonnerErrorOptions = NonNullable<Parameters<SonnerError>[1]>;
type SonnerToasterProps = React.ComponentProps<typeof SonnerToaster>;
type EnhancedToast = typeof sonnerToast & {
  error: (message: Parameters<SonnerError>[0], data?: ToastErrorOptions) => ReturnType<SonnerError>;
};

/**
 * Configuration options for displaying an error toast, extending Sonner's default options.
 */
export type ToastErrorOptions = SonnerErrorOptions & {
  /** The underlying error object or response payload. */
  error?: unknown;
  /** Whether the toast should remain visible until explicitly dismissed. */
  persistent?: boolean;
  /** Whether to hide the "Details" button that opens the error modal. */
  suppressDetails?: boolean;
  /** Custom label for the details button. Defaults to "Details". */
  detailsLabel?: ReactNode;
};

const originalError = sonnerToast.error.bind(sonnerToast);

const mergeClassNames = (...classNames: Array<string | undefined>) => classNames.filter(Boolean).join(" ");

const createErrorToastId = (error: ReturnType<typeof normalizeAppError>) => {
  if (!error.traceId && !error.correlationId && error.statusCode === 0) {
    return undefined;
  }

  return `${error.statusCode}-${error.traceId ?? error.correlationId ?? error.message}`;
};

const patchedError: SonnerError = (message, data) => {
  const options = (data ?? {}) as ToastErrorOptions;
  const { error, persistent, suppressDetails, detailsLabel, action, cancel, closeButton, duration, id, ...rest } = options;
  const fallbackTitle = toPlainText(message) ?? undefined;
  const fallbackMessage = toPlainText(rest.description) ?? fallbackTitle;
  const appError = suppressDetails
    ? undefined
    : normalizeAppError(error, {
        title: fallbackTitle,
        message: fallbackMessage,
        debugContext: buildClientDebugContext(new Error().stack),
      });

  const hasErrorObject = Boolean(error);
  const nextId = id ?? ((persistent || hasErrorObject) ? createErrorToastId(appError!) : undefined);
  let nextDescription = rest.description;

  trackProductEvent("frontend.toast.error.shown", {
    has_error_object: hasErrorObject,
    has_details: Boolean(hasErrorObject && appError && !suppressDetails),
    persistent: Boolean(persistent),
    suppress_details: Boolean(suppressDetails),
    status_code: appError?.statusCode ?? 0,
    entity: appError?.entity ?? appError?.error ?? "unknown",
  });

  if (appError?.location?.path) {
    return nextId ?? "";
  }

  if (hasErrorObject && appError && !suppressDetails) {
    nextDescription = React.createElement(
      "div",
      { className: "flex flex-col gap-1 mt-1" },
      React.createElement(
        "button",
        {
          type: "button",
          onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            if (nextId) {
              sonnerToast.dismiss(nextId);
            }
            openErrorDetails(appError);
          },
          className: "text-[12px] opacity-70 hover:opacity-100 underline underline-offset-2 text-left transition-opacity w-fit font-medium",
        },
        detailsLabel ?? "Details"
      )
    );
  }

  return originalError(message, {
    ...rest,
    description: nextDescription,
    id: nextId,
    action,
    cancel,
    closeButton: closeButton ?? persistent,
    duration: persistent && duration === undefined ? Infinity : duration,
  });
};

(sonnerToast as EnhancedToast).error = patchedError;

/**
 * Shared Toaster wrapper that keeps toast actions interactive even while Radix dialogs are open.
 */
export const Toaster = ({ className, toastOptions, ...props }: SonnerToasterProps) =>
  React.createElement(SonnerToaster, {
    ...props,
    className: mergeClassNames("pointer-events-auto", className),
    toastOptions: {
      ...toastOptions,
      classNames: {
        ...toastOptions?.classNames,
        toast: mergeClassNames("pointer-events-auto", toastOptions?.classNames?.toast),
      },
    },
  });

/**
 * An enhanced version of the Sonner toast utility.
 * Automatically normalizes application errors, reports telemetry, and provides a "Details" action
 * to inspect complex errors in a dedicated modal.
 */
export const toast = sonnerToast as EnhancedToast;
